import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import DoneIcon from '@material-ui/icons/Done';
import { skipToken } from '@reduxjs/toolkit/query';
import { isNil } from 'lodash';
import { useEffect, useRef, useState, useContext } from 'react';
import TagManager from 'react-gtm-module';
import { useSelector, useDispatch } from 'react-redux';
import { Redirect, useHistory, useLocation } from 'react-router-dom';
import { ACCOUNT_EXISTS_URL, AnalyticsContext, BREAD_LENDER_STATUS, CREDIT_PORTAL_URL, DECLINED_URL, PRE_QUALIFICATION_ACCEPT_DECLINED_URL, PRE_QUALIFICATION_INFORMATION_REQUIRED_URL, PRIME_LENDERS, QUERY_KEY, STEP_UP_REQUIRED_URL, apiErrorToErrorMessage, approveTerms, determinePrequalificationError, formatCurrency, parseQuery, reportError, useIovation, useParentMessaging, PROGRAM_PRODUCT_TYPE_FLEX, } from 'shared';
import { useGetConsumerApplicationsV1LocationsLocationIdLenderTermsLenderNameQuery } from 'consumer-applications-v1-api';
import { usePostConsumerApplicationsV2CreditApplicationsApplicationIdApplyMutation, usePostConsumerApplicationsV2CreditApplicationsApplyAfterPrequalificationMutation, usePostConsumerApplicationsV2PrequalificationsApplicationIdNextLenderMutation, } from 'consumer-applications-v2-api';
import { determinePrequalificationResponse, toastError, flashSubmitFailure, } from 'utils';
import { setLenderReferenceId, setLenderName, setPendingDecisionDetails, setPrequalification, setApprovedApplication, setActiveOfferId, setOfferScreen, } from '../actions';
import { ACIPrequalApproval, ImprintPresentedOffer, PrequalAcceptDeclineModal, } from '../components';
import PreQualificationForm from '../forms/PreQualificationForm';
import { selectors } from '../reducer';
import PreQualificationSubprime from './PreQualificationSubprime';
function PreQualification({ payload, onStepComplete, applicationEnvironment, }) {
    const getToken = () => {
        const query = parseQuery(location.search);
        const token = query[QUERY_KEY.TOKEN];
        return token;
    };
    //CUSTOM HOOKS
    const [getBlackboxData] = useIovation();
    const history = useHistory();
    const location = useLocation();
    const dispatch = useDispatch();
    const { sendCreditDecision } = useParentMessaging();
    const partnerTrackingGuid = parseQuery(location.search)['partner_tracking_guid'];
    //COMPONENT STATE
    const [preQualDeclineModalShow, setPreQualDeclineModalShow] = useState(false);
    const [declinedPayload, setDeclinedPayload] = useState(null);
    const [waterFalling, setWaterfalling] = useState(false);
    const [terms, setTerms] = useState(null);
    const tokenRef = useRef(getToken());
    const { analytics } = useContext(AnalyticsContext);
    //REDUX STATE
    const personalInfo = useSelector(selectors.personalInfo);
    const practice = useSelector(selectors.practice);
    const prequalification = useSelector(selectors.prequalification);
    const activeOfferId = useSelector(selectors.activeOfferId);
    const offerScreen = useSelector(selectors.offerScreen);
    const { firstName, lastName } = personalInfo;
    const { offers, applicationServiceProviderPriority } = prequalification;
    const activeOffer = activeOfferId
        ? offers.find((offer) => offer.offerId === activeOfferId)
        : offers.find((offer) => offer.offerType !== 'not_prequalified');
    const lenderName = offers && activeOffer && activeOffer.serviceProviderName;
    const lenderProductType = offers && activeOffer && activeOffer.lenderProductType;
    const { amount: prequalifiedCreditLimit } = activeOffer
        ? activeOffer
        : { amount: 0 };
    const isPrequalified = prequalification.applicationStatus === 'prequalified';
    const applyForLineOfCredit = lenderName === 'ADS' || lenderName === undefined;
    const submitButtonText = applyForLineOfCredit
        ? ' ACCEPT OFFER AND SUBMIT APPLICATION'
        : 'ACCEPT OFFER';
    //API HOOKS
    const [applyAfterPrequalificationV2] = usePostConsumerApplicationsV2CreditApplicationsApplyAfterPrequalificationMutation();
    const [prequalifyWithNextLenderV2] = usePostConsumerApplicationsV2PrequalificationsApplicationIdNextLenderMutation();
    const { data: lenderTerms } = useGetConsumerApplicationsV1LocationsLocationIdLenderTermsLenderNameQuery(lenderName === 'ADS' || lenderName === undefined
        ? {
            locationId: prequalification.location.locationId,
            lenderName: 'ADS',
        }
        : skipToken);
    const [submitFullApplicationV2, {}] = usePostConsumerApplicationsV2CreditApplicationsApplicationIdApplyMutation();
    //HANDLERS
    const getApplyAfterPrequalArgs = (offer, authorizedBuyer) => {
        const { applicationId } = prequalification;
        const acceptedOffer = offer;
        const { offerId: acceptedOfferId, terms: acceptedOfferTerms } = acceptedOffer ? acceptedOffer : { offerId: 0, terms: [] };
        const blackboxData = getBlackboxData();
        const approvedTerms = acceptedOfferTerms
            ? approveTerms(...acceptedOfferTerms)
            : [];
        const application = {
            applicationId,
            acceptedOfferId,
            iovationBlackBox: blackboxData.blackbox,
            prerequisiteTermsAccepted: approvedTerms,
            ...(authorizedBuyer ? authorizedBuyer : {}),
        };
        return application;
    };
    const handleSubmit = async (authorizedBuyer) => {
        const { applicationId } = prequalification;
        const blackboxData = getBlackboxData();
        if (location.state?.fullApp) {
            const application = {
                applicationId,
                iovationBlackBox: blackboxData.blackbox,
                prerequisiteTermsAccepted: terms.map(({ termContentId }) => ({
                    termContentId,
                    acceptedOn: new Date().toISOString(),
                })),
                ...authorizedBuyer,
            };
            const res = await submitFullApplicationV2({
                applicationId,
                postConsumerApplicationsV2CreditApplicationsApplicationIdApply: application,
            });
            if ('data' in res) {
                dispatch(setApprovedApplication(res.data));
            }
            return res;
        }
        else {
            const application = getApplyAfterPrequalArgs(activeOffer, authorizedBuyer);
            const res = await applyAfterPrequalificationV2({
                postConsumerApplicationsV2CreditApplicationsApplyAfterPrequalification: application,
            });
            if ('data' in res) {
                dispatch(setApprovedApplication(res.data));
            }
            return res;
        }
    };
    const handleChoosePrimeOffer = async (offer) => {
        const application = getApplyAfterPrequalArgs(offer);
        const res = await applyAfterPrequalificationV2({
            postConsumerApplicationsV2CreditApplicationsApplyAfterPrequalification: application,
        });
        if ('data' in res) {
            dispatch(setApprovedApplication(res.data));
            handleSubmitSuccess(res);
        }
        else {
            handleDecline(applicationServiceProviderPriority);
        }
    };
    const handleDecline = (applicationServiceProviderPriority) => {
        if (applicationServiceProviderPriority &&
            applicationServiceProviderPriority.productType === 'installment') {
            history.push(PRE_QUALIFICATION_ACCEPT_DECLINED_URL);
            //declined after prequal for both
        }
        else {
            //declined after prequal for bread
            history.push(DECLINED_URL);
        }
    };
    const handleSubmitSuccess = ({ data, }) => {
        setWaterfalling(true);
        const { applicationStatus, locationServiceProviderPriorities, applicationServiceProviderPriority, applicationId, lenderRequestReferenceId, pendingDecisionDetails, lenderStatusMessage, } = data;
        dispatch(setLenderReferenceId(lenderRequestReferenceId));
        dispatch(setPendingDecisionDetails(pendingDecisionDetails));
        const creditInstrument = data;
        sendCreditDecision({
            applicationId,
            partnerTrackingGuid,
            status: applicationStatus,
            availableCredit: creditInstrument.creditLimit ?? null,
            accountMember: creditInstrument.accountMemberId ?? null,
            consumerCreditInstrument: creditInstrument.consumerCreditInstrumentId ?? null,
            cardNumber: creditInstrument.alphaeonAccountNumber ?? null,
        });
        setDeclinedPayload({
            ...payload,
            applicationId,
        });
        if (applicationStatus === 'approved' &&
            data.programProductType ===
                PROGRAM_PRODUCT_TYPE_FLEX) {
            dispatch(setLenderName('ACI'));
            analytics?.track('ApplicationApproved', {});
            onStepComplete();
        }
        else if (applicationStatus === 'approved') {
            analytics?.track('ApplicationApproved', {});
            onStepComplete();
        }
        else if (applicationStatus === 'account_exists') {
            history.push(ACCOUNT_EXISTS_URL);
        }
        else if (applicationStatus === 'pending' &&
            lenderStatusMessage === BREAD_LENDER_STATUS.STEP_UP_NEEDED) {
            history.push(STEP_UP_REQUIRED_URL);
        }
        else if (applicationStatus === 'pending') {
            history.push(PRE_QUALIFICATION_INFORMATION_REQUIRED_URL);
        }
        else if (applicationStatus === 'prequalified' ||
            applicationStatus === 'pending_approval') {
            if (offers.length > 1 &&
                offers.some((offer) => offer.offerType === 'prequalified' &&
                    PRIME_LENDERS.includes(offer.serviceProviderName))) {
                // This allows the user to select a different prime offer if they have one
                handleDecline(applicationServiceProviderPriority);
                return;
            }
        }
        else if (applicationStatus === 'declined') {
            const nextLender = locationServiceProviderPriorities.consumer.some(({ priority }) => {
                if (applicationServiceProviderPriority) {
                    return priority > applicationServiceProviderPriority.priority;
                }
                else {
                    return false;
                }
            });
            if (nextLender) {
                const blackboxData = getBlackboxData();
                return prequalifyWithNextLenderV2({
                    applicationId,
                    postConsumerApplicationsV2PrequalificationsApplicationIdNextLender: {
                        iovationBlackBox: blackboxData.blackbox,
                    },
                })
                    .then(handleNextLenderResponse)
                    .catch(handleNextLenderError);
            }
            else {
                handleDecline(applicationServiceProviderPriority);
                return;
            }
        }
        else {
            throw new Error('Not Handled');
        }
        setWaterfalling(false);
    };
    const handleNextLenderResponse = (response) => {
        if ('data' in response) {
            dispatch(setLenderReferenceId(response.data.lenderRequestReferenceId));
            const offers = response.data.offers ?? [];
            const offerToUse = offers.find((offer) => offer.offerType !== 'not_prequalified');
            dispatch(setActiveOfferId(offerToUse ? offerToUse.offerId : undefined));
            dispatch(setOfferScreen('PRESENTED OFFER'));
            const nextStep = determinePrequalificationResponse(response.data, 'credit');
            dispatch(setPrequalification(response.data));
            history.push(nextStep);
            setWaterfalling(false);
            return;
        }
    };
    const handleNextLenderError = (error) => {
        setWaterfalling(false);
        const nextStep = determinePrequalificationError(error);
        if (nextStep) {
            history.push(nextStep);
            return;
        }
        const errMsg = apiErrorToErrorMessage(error?.response?.errorCode, error?.response?.message);
        reportError('flashError', {
            componentName: 'PreQualification',
            funcName: 'handleSubmitSuccess',
            errType: 'api',
            errURL: window.location.pathname,
            errMsg: errMsg,
        }, error);
        toastError(errMsg);
    };
    //EFFECTS
    useEffect(() => {
        TagManager.dataLayer({
            dataLayer: {
                event: 'conversion',
            },
        });
    }, []);
    useEffect(() => {
        const unblock = history.block((location, action) => {
            if (action === 'POP' && !isNil(tokenRef.current)) {
                // One Time Apps back button -> no action
                // Return true to allow transition, false to block
                return false;
            }
        });
        return () => {
            unblock();
        };
    }, []);
    useEffect(() => {
        if (offers && activeOffer && activeOffer.terms) {
            setTerms(activeOffer.terms);
        }
        else if (applyForLineOfCredit && lenderTerms?.terms) {
            setTerms(lenderTerms.terms);
        }
    }, [prequalification, lenderTerms]);
    useEffect(() => {
        if (prequalification) {
            if (offers && activeOffer && activeOffer.serviceProviderName) {
                dispatch(setLenderName(lenderName));
            }
            else {
                dispatch(setLenderName(''));
            }
        }
        else {
            dispatch(setLenderName(''));
        }
    }, [prequalification]);
    useEffect(() => {
        if (activeOfferId === undefined && activeOffer) {
            dispatch(setActiveOfferId(activeOffer.offerId));
        }
    }, []);
    useEffect(() => {
        window.scrollTo(0, 0);
        return () => {
            dispatch(setActiveOfferId(undefined));
            dispatch(setOfferScreen('PRESENTED OFFER'));
        };
    }, []);
    if (isNil(prequalification))
        return _jsx(Redirect, { to: CREDIT_PORTAL_URL });
    return (_jsx(_Fragment, { children: lenderName !== 'Imprint' ? (_jsxs("div", { children: [offerScreen !== 'OTHER OFFERS' &&
                    (applyForLineOfCredit ? (_jsxs("div", { className: "pre-qualification-card", children: [isPrequalified ? (_jsxs("div", { children: [_jsxs("p", { className: "first-title", children: ["Congratulations, ", firstName, " ", lastName] }), _jsx("p", { className: "first-subtitle", children: "You're pre-qualified for the Alphaeon Credit Card" })] }, "prequalified")) : (_jsxs("div", { children: [_jsxs("p", { className: "first-title", children: [' ', firstName, " ", lastName] }), _jsx("p", { className: "first-subtitle", children: _jsx("span", { className: "not-important", children: "We were unable to pre-qualify you, however you may still submit an application. Submitting a full application will result in a hard credit inquiry, which can negatively impact your credit score." }) })] }, "not-prequalified")), _jsxs("div", { className: "first-card", children: [isPrequalified && (_jsxs("div", { className: "first-card-header", children: [_jsxs("p", { className: "first-limit", children: ["PRE-QUALIFIED CREDIT LIMIT +", ' '] }), _jsx("p", { className: "first-price", children: formatCurrency(prequalifiedCreditLimit, 2) }), _jsx("div", { className: "first-detail", children: "+ The credit limit provided is based on your pre-qualification only, and upon submission of your full credit application, may be increased or decreased." })] })), _jsxs("div", { className: "first-card-body", children: [_jsx("p", { className: "first-card-body-header", children: "Cardmember Benefits:" }), _jsxs("p", { className: "first-card-body-sub-header bold", children: ["Patient Financing offered by Dentists, Dermatologists,", ' ', _jsx("br", {}), "Plastic Surgeons, and Ophthalmologists"] }), _jsx("br", {}), _jsxs("div", { className: "flex", children: [_jsx("div", { className: "first-card-icon-container m-b-10", children: _jsx(DoneIcon, { className: "first_tick" }) }), _jsx("p", { children: "Special financing options* for any purchase over $250" })] }), _jsxs("div", { className: "flex", children: [_jsx("div", { className: "first-card-icon-container m-b-10", children: _jsx(DoneIcon, { className: "first_tick" }) }), _jsxs("p", { children: ["Ability to reuse your card again and again with Dentists, ", _jsx("br", {}), " Dermatologists, Ophthalmologists, and Plastic Surgeons nationwide at participating locations."] })] }), _jsx("div", { className: "italic", children: _jsxs("p", { children: ["Alphaeon Credit Card Accounts are issued by Comenity Capital Bank.", _jsx("br", {}), "Credit card offers are subject to credit approval.", _jsx("br", {}), "\"Minimum payments are required for each Credit Plan. Valid for single transaction only."] }) })] })] })] })) : lenderName === 'ACI' ? (_jsx(ACIPrequalApproval, { practiceName: practice.name, approvalAmount: prequalifiedCreditLimit, offer: activeOffer, handleAcceptOffer: handleChoosePrimeOffer })) : (_jsx(_Fragment, { children: _jsx(PreQualificationSubprime, { prequalifiedLoanAmount: formatCurrency(prequalifiedCreditLimit, 2), practice: practice, lenderName: lenderName, lenderProductType: lenderProductType }) }))), terms && lenderName !== 'ACI' ? (_jsx(PreQualificationForm, { personalInfo: personalInfo, lenderName: lenderName, onSubmit: handleSubmit, onSubmitSuccess: handleSubmitSuccess, onSubmitFail: flashSubmitFailure, practice: practice, applicationEnvironment: applicationEnvironment, terms: terms, submitButtonText: submitButtonText, showTerms: applyForLineOfCredit, waterFalling: waterFalling, prequalification: prequalification, submitting: false, handleAcceptOffer: handleChoosePrimeOffer })) : null, preQualDeclineModalShow && (_jsx(PrequalAcceptDeclineModal, { payload: declinedPayload, onClose: () => setPreQualDeclineModalShow(false) }))] }, "not-imprint")) : (_jsx("div", { children: _jsx(ImprintPresentedOffer, { practiceName: practice.name, activeOffer: activeOffer, offers: offers, handleAcceptOffer: handleChoosePrimeOffer }) }, "imprint")) }));
}
export default PreQualification;
